/**
 * Application state.
 *
 * Keeping the mutable data in one place makes it explicit which part of the
 * application can change and prevents DOM code from becoming the state store.
 */

import { 
  PERIODS,
  SPECIAL_EXT,
  DEMO_ENTRIES,
  PALETTE,
  ALPHA_COLOR
} from "./settings.mjs";

export class AppState {
  constructor() {
    // Standardkonfiguration (Kernekonfiguration)
    /** @type {AppConfig} */
    this.config = {
      periods: PERIODS, // Standard perioder
      special_ext: SPECIAL_EXT, // Særlig gentagelsesvirkning
      alpha_color: ALPHA_COLOR
    };
    /** @type {EntryList} */
    this.entries = [];
    /** @type {readonly RawDemoEntry[]} */
    this.demo_entries = DEMO_ENTRIES;
    /** @type {boolean} */
    this.demoActive = false;
    /** @type {Map<string, ModifierCallback[]>} */
    this.modifiers = new Map(); // Holder på dynamiske funktioner fra eksperimenter
    /** @type {Set<string>} */
    this.activeExperiments = new Set();
    /** @type {string[]} */
    this.palette = PALETTE;
  }

  /**
   * Gør det muligt for eksperimentelle moduler at overskrive kernekonfigurationer
   * @template {keyof AppConfig} K
   * @param {K} key 
   * @param {AppConfig[K]} newValue 
   * @returns {void}
   */
  updateConfig(key, newValue) {
    if (key in this.config) {
      this.config[key] = newValue;
    }
  }

  /**
   * Henter en aktuel konfigurationsværdi
   * @template {keyof AppConfig} K
   * @param {K} key
   * @returns {AppConfig[K]}
   */
  getConfig(key) {
    return this.config[key];
  }

  /**
   * Gør det muligt for et eksperiment at registrere en funktion til at ændre specifik logik
   * @param {string} featureName 
   * @param {ModifierCallback} modifierFn 
   * @returns {void}
   */
  registerModifier(featureName, modifierFn) {
    let list = this.modifiers.get(featureName);
    if (!list) {
      list = [];
      this.modifiers.set(featureName, list);
    }
    list.push(modifierFn);
  }

  // Kører alle registrerede ændringer på et givet objekt (f.eks. en Luxon Duration)
  /**
   * @template T
   * @param {string} featureName 
   * @param {T} data 
   * @param {...any} args 
   * @returns {T}
   */
  applyModifiers(featureName, data, ...args) {
    let modifiedData = data;
    const list = this.modifiers.get(featureName);
    if (list) {
      for (const modifier of list) {
        modifiedData = modifier(modifiedData, ...args);
      }
    }
    return modifiedData;
  }
}